import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import StarRating from '../components/StarRating';

function timeAgo(dateStr) {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString('en-AE', { day: 'numeric', month: 'short' });
}

function SkeletonCard() {
  return (
    <div className="bg-dark-surface border border-dark-border rounded-2xl p-4 animate-pulse">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-dark-bg flex-shrink-0" />
        <div className="flex-1">
          <div className="h-3.5 bg-dark-bg rounded-lg w-1/3 mb-1.5" />
          <div className="h-3 bg-dark-bg rounded-lg w-1/4" />
        </div>
      </div>
      <div className="h-3 bg-dark-bg rounded-lg w-full mb-1.5" />
      <div className="h-3 bg-dark-bg rounded-lg w-2/3" />
    </div>
  );
}

function ReviewCard({ review, type }) {
  const user = type === 'given' ? review.provider || review.toUser : review.client || review.fromUser;
  const name = user?.name || user?.fullName || 'User';
  const avatar = user?.avatar || user?.profilePicture;

  return (
    <div className="bg-dark-surface border border-dark-border rounded-2xl p-4">
      {/* User info */}
      <div className="flex items-center gap-3 mb-2.5">
        {avatar ? (
          <img src={avatar} alt={name} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-brand-purple/20 flex items-center justify-center flex-shrink-0">
            <span className="text-brand-purple text-sm font-bold">{name.charAt(0).toUpperCase()}</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-dark-text truncate">{name}</p>
          {review.service?.title && (
            <p className="text-dark-subtext text-xs truncate">{review.service.title}</p>
          )}
        </div>
        <p className="text-dark-subtext/60 text-xs flex-shrink-0">{review.createdAt ? timeAgo(review.createdAt) : ''}</p>
      </div>

      {/* Rating */}
      <div className="mb-2">
        <StarRating rating={review.rating} />
      </div>

      {/* Comment */}
      {review.comment && (
        <p className="text-dark-subtext text-sm leading-relaxed">{review.comment}</p>
      )}
    </div>
  );
}

export default function MyReviewsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('given');
  const [givenReviews, setGivenReviews] = useState([]);
  const [receivedReviews, setReceivedReviews] = useState([]);
  const [loadingGiven, setLoadingGiven] = useState(true);
  const [loadingReceived, setLoadingReceived] = useState(true);

  const fetchGiven = useCallback(async () => {
    setLoadingGiven(true);
    try {
      const { data } = await api.get('/reviews/given');
      setGivenReviews(Array.isArray(data) ? data : (data.reviews || []));
    } catch {
      setGivenReviews([]);
    }
    setLoadingGiven(false);
  }, []);

  const fetchReceived = useCallback(async () => {
    setLoadingReceived(true);
    try {
      const { data } = await api.get('/reviews/received');
      setReceivedReviews(Array.isArray(data) ? data : (data.reviews || []));
    } catch {
      setReceivedReviews([]);
    }
    setLoadingReceived(false);
  }, []);

  useEffect(() => {
    fetchGiven();
    fetchReceived();
  }, [fetchGiven, fetchReceived]);

  const reviews = tab === 'given' ? givenReviews : receivedReviews;
  const loading = tab === 'given' ? loadingGiven : loadingReceived;

  return (
    <div className="min-h-screen bg-dark-bg pb-24">
      {/* Header */}
      <div className="px-4 pt-12 pb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-dark-subtext">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <h1 className="text-xl font-bold text-dark-text" style={{ letterSpacing: '-0.5px' }}>My Reviews</h1>
      </div>

      {/* Tabs */}
      <div className="px-4 mb-4">
        <div className="flex bg-dark-surface border border-dark-border rounded-2xl p-1">
          <button
            onClick={() => setTab('given')}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-xl transition-colors ${
              tab === 'given'
                ? 'bg-brand-purple text-white'
                : 'text-dark-subtext'
            }`}
          >
            Reviews I Gave
          </button>
          <button
            onClick={() => setTab('received')}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-xl transition-colors ${
              tab === 'received'
                ? 'bg-brand-purple text-white'
                : 'text-dark-subtext'
            }`}
          >
            Reviews I Received
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 space-y-3">
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : reviews.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-20 h-20 rounded-full bg-dark-surface border border-dark-border flex items-center justify-center mb-5">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-dark-subtext/50">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
            </div>
            <p className="text-dark-text font-semibold text-lg mb-1">No reviews yet</p>
            <p className="text-dark-subtext text-sm">
              {tab === 'given'
                ? 'Reviews you write for providers will appear here.'
                : 'Reviews from your clients will appear here.'}
            </p>
          </div>
        ) : (
          reviews.map((review) => (
            <ReviewCard key={review.id || review._id} review={review} type={tab} />
          ))
        )}
      </div>
    </div>
  );
}
